import React, { useEffect, useMemo, useState, useRef } from 'react'
import { CSSTransition } from 'react-transition-group'
import { useSelector } from 'react-redux'
import {
    useParams,
    useNavigate,
    useSearchParams,
    useLocation
} from 'react-router-dom'
import {
    Col,
    Container,
    Row,
    TopBar,
    CardClientDetails,
    Button,
    Spinner,
    CelebrationPopupContent,
    PopupModal
} from '../../components'
import {
    useBackAction,
    useBindDispatch,
    useGetClient,
    useAnalytics,
    useInteractions
} from 'hooks'
import { VillageClientPacket, VillageClientPayment } from './'
import {
    constants,
    getConnectUser,
    getStorage,
    isClientInfoComplete,
    routes
} from '../../helpers'
import { useTranslation } from 'react-i18next'
import ClientStatus from '../../components/card/ClientStatus'
import { ClientStatus as ClientStatusEnum } from '../../storeTypes/clientTypes'
import { findPermission } from '../../helpers/findPermission'
import { getClientTransactionDetails } from 'pages/payment/helpers/getClientTransactionData'
import { buildPaymentSummaryProps } from 'pages/payment/helpers/buildPaymentSummaryProps'
import { translateGender } from './helpers/villageClientHelpers'
import { isAnyDelivered } from 'helpers/isAnyDelivered'
import { PaymentFeedbackMessage } from 'components/enrollForm'

export const VillageClientDetails = () => {
    const [animate, setAnimate] = useState(false)
    const [animationList, setAnimationList] = useState(false)
    const [showMobilePaymentModal, setShowMobilePaymentModal] = useState(false)

    const [
        displayFinisherCelebrationPopup,
        setDisplayFinisherCelebrationPopup
    ] = useState(false)

    const [celebrationDataLoaded, setCelebrationDataLoaded] = useState(false)

    const { pathname, state } = useLocation()
    const [searchParams] = useSearchParams()
    const reload = searchParams.get('reload')
    const fromURL = state?.from || searchParams.get('from')
    const mobilePaymentStatus = searchParams.get('mobile-payment')
    const mobileMoneyAmount = searchParams.get('mm-amount')
    const { villageId, clientId } = useParams()
    const { trackEvent } = useAnalytics()

    const nodeRef = useRef(null)

    const { t } = useTranslation()
    const navigate = useNavigate()

    const { clientOrders } = useSelector((state) => state.clientOrders)

    const { transactions } = useSelector(
        (state) => state.transactions[villageId] || {}
    )

    const {
        selectVillageClientHandler,
        navbarHandler,
        updateSingleClientBalAndSmsHandler,
        clearSingleClientDataHandler,
        getClientOrdersHandler,
        resetClientOrdersHandler,
        transactionsHandler,
        getPaymentCelebrationDataHandler,
        updatePaymentCelebrationDataHandler,
        clearMobilePaymentHandler,
        setLastVisitedClientHandler
    } = useBindDispatch()
    const { country, veVillages, username } = getConnectUser()
    const { selectedClient } = useSelector(
        (state) => state.villageClientList[villageId] || {}
    )

    const { selectedVeVillages } = useSelector((state) => state.ve)

    const { paymentCelebrationState } = useSelector(
        (state) => state.paymentCelebrationState
    )

    const villages = selectedVeVillages || veVillages

    const { client, loadingClient } = useGetClient(selectedClient, clientId)

    const saveClientInLocalStore = (clientData) => {
        localStorage.setItem(
            constants.CLIENT_FOR_ENROLLMENT_STORE,
            JSON.stringify({
                ...clientData,
                goal_items: clientOrders?.filter((item) => !item.delivered)
            })
        )
    }

    const modifyHandler = (modificationType, nextURL = '') => {
        if (modificationType) {
            client.form_type = constants.FORM_TYPES.MODIFICATION
            client.village_name = villageName
            client.village_id = villageId
            const next = nextURL ? `&next=${nextURL}` : ''
            saveClientInLocalStore({
                ...client,
                modification: modificationType.includes('package')
                    ? constants.CLIENT_MODIFICATION_TYPES.PACKAGES
                    : constants.CLIENT_MODIFICATION_TYPES.DETAILS
            })
            return navigate(
                `/${routes.enroll}/${client.village_id}?from=${pathname}${next}`
            )
        }
    }

    const { interactionHandler } = useInteractions({
        client,
        username,
        villageId,
        modifyHandler
    })

    const { clientBalance, lastPaymentDate, transactionsBalance } = useMemo(
        () =>
            getClientTransactionDetails(
                client || { client_code: '', balance: 0 },
                transactions
            ),
        [client, transactions]
    )

    const userPermissions = getStorage(constants.USER_PERMISSIONS)

    const url = `/village/${villageId}/`

    // handles on device back button press or back swipe
    useBackAction(fromURL || url)

    const canModifyClient = !client?.duplicate

    const displayVisitButton =
        findPermission(userPermissions, constants.CAN_RECORD_HOME_VISIT) &&
        canModifyClient

    const { name: villageName } = villages?.find(
        ({ sf_id: sfId }) => sfId === villageId
    ) || { name: '' }
    const { openVillagePageHandler } = useBindDispatch()

    const isClientCertified = useMemo(() => {
        return client?.certified_for_delivery === 'True'
    }, [client])

    useEffect(() => {
        if (reload) {
            updateSingleClientBalAndSmsHandler({
                villageId,
                clientId
            })
        }
    }, [reload])

    // show menu bar
    useEffect(() => {
        setAnimate(true)
        navbarHandler(constants.SHOW_NAVBAR)
        document.title = t('clientDetails.pageTitle')

        return () => {
            clearSingleClientDataHandler()
        }
    }, [])

    useEffect(() => {
        selectVillageClientHandler({ clientId, villageId })
        getClientOrdersHandler(clientId)
    }, [])

    useEffect(() => {
        transactionsHandler({ villageId })
    }, [])

    useEffect(() => {
        return () => {
            resetClientOrdersHandler()
        }
    }, [])

    useEffect(() => {
        setCelebrationDataLoaded(false)

        const loadCelebrationData = async () => {
            try {
                await getPaymentCelebrationDataHandler(clientId).unwrap()
                setCelebrationDataLoaded(true)
            } catch {
                // Keep celebrationDataLoaded false so stale paymentCelebrationState
                // from another client is not acted on after a failed worker read.
            }
        }

        loadCelebrationData()
    }, [clientId])

    useEffect(() => {
        if (
            celebrationDataLoaded &&
            client?.all_enrollment > 0 &&
            clientBalance >= client?.all_enrollment &&
            paymentCelebrationState?.seenCelebration === false
        ) {
            setDisplayFinisherCelebrationPopup(true)
        } else if (
            celebrationDataLoaded &&
            transactionsBalance > 0 &&
            transactionsBalance < client?.all_enrollment &&
            paymentCelebrationState?.seenCelebration === true
        ) {
            updatePaymentCelebrationDataHandler({
                client_id: client?.client_code
            })
        }
    }, [
        celebrationDataLoaded,
        paymentCelebrationState?.seenCelebration,
        transactionsBalance,
        client?.all_enrollment
    ])

    useEffect(() => {
        if (mobilePaymentStatus) {
            setShowMobilePaymentModal(true)
            if (mobilePaymentStatus === constants.MOBILE_MONEY.SUCCESS) {
                if (client?.client_code) {
                    setLastVisitedClientHandler(client.client_code)
                }
                // The mobile money page tracks its own outcome and always
                // navigates here with mobilePaymentTracked; only the provider's
                // redirect back arrives without router state. That redirect is
                // today the only outcome signal: Connect's payment status is
                // not updated by the mobile money callback, so the originating
                // tab's polling never reaches success. Once that callback is
                // wired, the polling tab tracks the outcome and this redirect
                // tracking (success and failure) must go, or it double-counts.
                if (!state?.mobilePaymentTracked) {
                    trackEvent({
                        category: constants.ANALYTICS.CATEGORIES.PAYMENT,
                        action: constants.ANALYTICS.ACTIONS
                            .MOBILE_MONEY_PAYMENT,
                        name: constants.ANALYTICS.NAMES.PAYMENT_SUCCESS,
                        value: 0
                    })
                }
            } else if (
                mobilePaymentStatus === constants.MOBILE_MONEY.FAILURE &&
                !state?.mobilePaymentTracked
            ) {
                // Provider error_url redirect; in-app navigation always passes
                // mobilePaymentTracked.
                trackEvent({
                    category: constants.ANALYTICS.CATEGORIES.PAYMENT,
                    action: constants.ANALYTICS.ACTIONS.MOBILE_MONEY_PAYMENT,
                    name: constants.ANALYTICS.NAMES.PAYMENT_FAILURE,
                    value: 0
                })
            }
        }
    }, [mobilePaymentStatus])

    const backNavigationHandler = () => {
        openVillagePageHandler({
            filterOptions: [],
            villageId,
            clientTab: constants.VILLAGE_PAGE_TABS.CURRENT_CLIENT_TAB
        })
        const navUrl = fromURL || url

        navigate(
            `${navUrl}?from_tab=${constants.VILLAGE_PAGE_TABS.CURRENT_CLIENT_TAB}`
        )
    }

    const openVisitPageHandler = () => {
        if (isClientInfoComplete(client)) {
            localStorage.setItem(
                constants.CLIENT_FOR_VISIT_STORE,
                JSON.stringify({
                    clientCode: client.client_code,
                    villageId: client.village_id || villageId,
                    clientName: client.firstname,
                    origin: constants.ORIGIN_CLIENT_DETAIL
                })
            )
            return navigate('/visit')
        } else {
            modifyHandler(constants.CLIENT_MODIFICATION_TYPES.DETAILS, '/visit')
        }
    }

    const closeMobileMoneyModal = () => {
        clearMobilePaymentHandler()
        setShowMobilePaymentModal(false)
    }

    const villageClientDetailsRender = (clientDetailsData) => {
        const {
            fullname,
            client_code: clientCode,
            sex: gender,
            group_leader: groupLeader,
            head_of_household: headOfHouseHold,
            zone,
            phone,
            all_enrollment: allEnrollment,
            age
        } = clientDetailsData

        return (
            <>
                <div className="sticky-header" id="sticky-header">
                    <Container>
                        <TopBar back backNavigation={backNavigationHandler}>
                            {villageName}
                        </TopBar>
                    </Container>
                </div>
                <CSSTransition
                    unmountOnExit
                    timeout={constants.ANIMATION_TIMEOUT}
                    in={animate}
                    classNames="generic"
                    appear
                    onEnter={() => setAnimationList(true)}
                    nodeRef={nodeRef}
                >
                    <div
                        className="village-client-details-parent page-wrapper"
                        data-testid="client-details-page"
                        ref={nodeRef}
                    >
                        <Container>
                            <Row>
                                <Col md={12} lg={4}>
                                    <ClientStatus
                                        status={
                                            client.duplicate
                                                ? ClientStatusEnum.DUPLICATE
                                                : client.client_status
                                        }
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4}>
                                    <CardClientDetails
                                        clientCode={clientCode}
                                        clientPhone={phone}
                                        interactionHandler={interactionHandler}
                                        otherClientData={{
                                            [t('gender')]: translateGender(
                                                gender,
                                                t
                                            ),
                                            [t('age')]: age,
                                            [t('groupRole')]: groupLeader,
                                            [t('familyLeader')]:
                                                headOfHouseHold,
                                            [t('zone')]: zone || '-'
                                        }}
                                        preCertified={isClientCertified}
                                        country={country}
                                        isClientDuplicate={client?.duplicate}
                                        clientStatus={client.client_status}
                                    >
                                        {fullname}
                                    </CardClientDetails>
                                </Col>
                                <Col md={8} className="display-flex fd-col">
                                    <Col
                                        md={12}
                                        className={
                                            animationList
                                                ? 'clear-h-padding flex-1 village-payment--col flex-0 animate-show-5 animate-showed-5'
                                                : 'clear-h-padding flex-1 village-payment--col flex-0 animate-show-5'
                                        }
                                    >
                                        <VillageClientPayment
                                            paymentSummary={buildPaymentSummaryProps(
                                                {
                                                    allEnrollment,
                                                    balance: clientBalance
                                                },
                                                lastPaymentDate
                                                    ? t('dateMonthYear', {
                                                          date: new Date(
                                                              lastPaymentDate
                                                          )
                                                      })
                                                    : '-'
                                            )}
                                            isAnyDelivered={isAnyDelivered(
                                                clientOrders
                                            )}
                                            fromURL={fromURL}
                                        />
                                    </Col>
                                    <Col
                                        md={12}
                                        className={
                                            animationList
                                                ? 'clear-h-padding flex-0 animate-show-3 animate-showed-3'
                                                : 'clear-h-padding flex-0 animate-show-3'
                                        }
                                    >
                                        <VillageClientPacket
                                            goalItems={clientOrders}
                                            allowModification={canModifyClient}
                                            modifyHandler={() =>
                                                modifyHandler(
                                                    constants
                                                        .CLIENT_MODIFICATION_TYPES
                                                        .PACKAGES
                                                )
                                            }
                                        />
                                    </Col>
                                    {displayVisitButton && (
                                        <div className="visit-new-container">
                                            <Button
                                                className="visit-new-btn"
                                                style="primary"
                                                onClick={openVisitPageHandler}
                                                data-testid="button-new-visit"
                                                id="btn-new-visit"
                                            >
                                                {t('enrollment.newVisit')}
                                            </Button>
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </Container>
                    </div>
                </CSSTransition>
                {displayFinisherCelebrationPopup && (
                    <CelebrationPopupContent
                        type="finisher"
                        onButtonClick={() => {
                            setDisplayFinisherCelebrationPopup(false)
                            updatePaymentCelebrationDataHandler({
                                client_id: client?.client_code,
                                seenCelebration: true
                            })
                        }}
                    />
                )}
                {showMobilePaymentModal && (
                    <PopupModal closeModal={closeMobileMoneyModal}>
                        <PaymentFeedbackMessage
                            amount={mobileMoneyAmount}
                            t={t}
                            status={mobilePaymentStatus}
                        />
                    </PopupModal>
                )}
            </>
        )
    }

    const ClientLoading = () => (
        <Container>
            <Row>
                <Col md={12}>
                    <div className="loading-wrapper">
                        <p className="loading-text">
                            {t('village.partialLoaderMsg')}
                        </p>
                        <Spinner size="120" />
                    </div>
                </Col>
            </Row>
        </Container>
    )

    if (!client || loadingClient) {
        return <ClientLoading />
    }

    return villageClientDetailsRender(client)
}
